import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Button } from 'react-bootstrap'
import BlockUi from '@availity/block-ui';
import { Form, Table } from 'rsuite';
import GenericPagination from '../../Shared/Grid/GenericPagination';
import LoadingSpinner from '../../Shared/GenericComponents/LoadingSpinner';
import paymentService from '../../../services/Internal/Revenue/payment.service';
import { useReactToPrint } from 'react-to-print';
import { FaPrint } from 'react-icons/fa';

function DailyRefundSearchPage() {
    const [isSearching, setIsSearching] = useState(false);
    const [printLoading, setPrintLoading] = useState(false);
    const componentRef = useRef(null);
    const promiseResolveRef = useRef(null);
    const [searchModel, setSearchModel] = useState({
        beginDate: '',
        endDate: ''
    })

    const handlePrint = useReactToPrint({
        documentTitle: 'Refund Report',
        contentRef: componentRef,
        onBeforePrint: () => {
            return new Promise((resolve) => {
                promiseResolveRef.current = resolve;
                setPrintLoading(true);
            });
        },
        onAfterPrint: () => {
            promiseResolveRef.current = null;
            setPrintLoading(false);
        }
    });
    const [searchResults, setSearchResults] = useState([]);
    useEffect(() => {
        // Initial search
        refreshGrid()
        refreshGrid()
    }, [])
    useEffect(() => {
        if (printLoading && promiseResolveRef.current) {
            // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
            promiseResolveRef.current();
        }
    }, [printLoading]);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [displayLength, setDisplayLength] = useState(20);
    const [page, setPage] = useState(1);
    const clearSearchModel = () => {
        setSearchModel({
            beginDate: '',
            endDate: '',
        })
    }
    const getPagePrintStyle = () => {
        return '@media print {.scroll-container {width:100% !important;height:100% !important;overflow: visible;} body{zoom:75%; display:block;}  .rs-table-row { break-inside: avoid; } @page{size:landscape}}';
    };
    const refreshGrid = (beginDate, endDate) => {
        setIsSearching(true);
        paymentService.searchDailyRefunds(beginDate, endDate).then((response) => {
            setSearchResults(response.data.data);
        }).finally(() => {
            setHasLoaded(true);
            setIsSearching(false);
        })
    }
    const submitSearch = () => {
        refreshGrid(searchModel.beginDate ? searchModel.beginDate : null, searchModel.endDate ? searchModel.endDate : null);
    }
    const getData = () => {
        return searchResults.filter((v, i) => {
            const start = displayLength * (page - 1);
            const end = start + displayLength;
            return i >= start && i < end;
        })
    }
    const filteredData = getData();
    let totalAmount = searchResults.reduce(function (total, currentValue) {
        return total + currentValue.refundAmount;
    }, 0)

    return <>
        <style>{getPagePrintStyle()}</style>
        <Card className="w-100 mb-3">
            <Card.Body>
                <BlockUi blocking={isSearching}>
                    <Form fluid formValue={searchModel} onChange={setSearchModel} onSubmit={submitSearch} >
                        <Row className="mb-3">
                            <Col>
                                <Form.Group>
                                    <Form.ControlLabel><b>Date Range</b></Form.ControlLabel>
                                    <Form.Control name="beginDate" type="date" block />
                                </Form.Group>
                            </Col>
                            <Col xs="auto" style={{ marginTop: '1.5em' }} className="d-flex align-items-center">to</Col>
                            <Col>
                                <Form.ControlLabel>&nbsp;</Form.ControlLabel>
                                <Form.Control name="endDate" type="date" block />
                            </Col>
                        </Row>

                        <hr />
                        <div style={{ textAlign: 'right' }}>
                            <Button onClick={clearSearchModel} variant="danger" className="me-2">Reset</Button>
                            <Button type="submit">Search</Button>
                        </div>
                    </Form>
                </BlockUi>
            </Card.Body>
        </Card >

        <Card className="w-100 mb-3">
            <Card.Body>
                <br />
                {hasLoaded ?
                    <>
                        <BlockUi blocking={printLoading}>
                            <div ref={componentRef}>
                                <Table wordWrap autoHeight data={filteredData} className="scroll-container">
                                <Table.Column width={100}>
                                    <Table.HeaderCell>
                                        <b>Payment ID</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => (rowData.paymentId)}</Table.Cell>
                                </Table.Column>
                                <Table.Column width={100}>
                                    <Table.HeaderCell>
                                        <b>Payment Date</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => (rowData.paymentDate ? new Date(rowData.paymentDate).toLocaleDateString() : '')}</Table.Cell>
                                </Table.Column>
                                <Table.Column width={150}>
                                    <Table.HeaderCell>
                                        <b>Payment Type</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => (rowData.paymentType)}</Table.Cell>
                                </Table.Column>
                                <Table.Column minWidth={175} flexGrow={1}>
                                    <Table.HeaderCell>
                                        <b>Chec#/CC Auth Number</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => (rowData.authorizationNumber)}</Table.Cell>
                                </Table.Column>
                                <Table.Column minWidth={150} flexGrow={1}>
                                    <Table.HeaderCell>
                                        <b>GL Account</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => {
                                        return (<>
                                            {rowData.glCodes.map((item, i) => {
                                                return (
                                                    <>
                                                        {item} <br />
                                                    </>
                                                )
                                            })}
                                        </>
                                        )
                                    }}</Table.Cell>
                                </Table.Column>
                                <Table.Column minWidth={150} flexGrow={1}>
                                    <Table.HeaderCell>
                                        <b>LOLA #</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => (rowData.paymentLolaNumber)}</Table.Cell>
                                </Table.Column>
                                <Table.Column minWidth={200} flexGrow={1}>
                                    <Table.HeaderCell>
                                        <b>Mailing Address</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => (<><p>{rowData.paymentFormattedName}</p><p>{rowData.address}</p><p>{rowData.city}, {rowData.stateCode}</p></>)}</Table.Cell>
                                </Table.Column>
                                <Table.Column width={100}>
                                    <Table.HeaderCell>
                                        <b>Refund Date</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => (rowData.refundDate ? new Date(rowData.refundDate).toLocaleDateString() : '')}</Table.Cell>
                                </Table.Column>
                                <Table.Column width={150}>
                                    <Table.HeaderCell>
                                        <b>Refund Amount</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => (rowData.refundAmount.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                    }))}</Table.Cell>
                                </Table.Column>
                                <Table.Column minWidth={200} flexGrow={1}>
                                    <Table.HeaderCell>
                                        <b>Reason</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => (rowData.refundReason)}</Table.Cell>
                                </Table.Column>
                                <Table.Column minWidth={200} flexGrow={1}>
                                    <Table.HeaderCell>
                                        <b>User</b>
                                    </Table.HeaderCell>
                                    <Table.Cell>{rowData => (rowData.refundUserFormattedName)}</Table.Cell>
                                </Table.Column>
                                </Table>
                                </div>
                            <GenericPagination dataLength={searchResults.length} setPage={setPage} page={page} displayLength={ displayLength} setDisplayLength={setDisplayLength} />
                        </BlockUi>
                        <Row>
                            <Col>
                                {
                                    <div>
                                        <Button variant="primary" onClick={handlePrint}><FaPrint/> Print</Button>
                                    </div>
                                }
                            </Col>
                            <Col className="text-end">
                                <h3> Total: {totalAmount.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                })}</h3>
                            </Col>
                        </Row>
                    </>
                    : <LoadingSpinner />
                }
            </Card.Body>
        </Card >
    </>;
}


export default DailyRefundSearchPage;